import type { ChangeEvent, ReactNode } from 'react';
import {
  Checkbox,
  FormControlLabel,
  Radio,
  RadioGroup,
  Switch,
  type FormControlLabelProps,
  type RadioGroupProps,
  type SxProps,
  type Theme,
} from '@mui/material';

export type ChoiceControlKind = 'checkbox' | 'radio' | 'switch';

export type ChoiceControlProps = {
  kind: ChoiceControlKind;
  label?: ReactNode;
  ariaLabel?: string;
  ariaLabelledBy?: string;
  ariaDescribedBy?: string;
  decorative?: boolean;
  checked?: boolean;
  indeterminate?: boolean;
  disabled?: boolean;
  name?: string;
  value?: string | number;
  size?: 'small' | 'medium';
  onCheckedChange?: (checked: boolean) => void;
  labelPlacement?: FormControlLabelProps['labelPlacement'];
  /** 일반 폼은 도형을 시작선에, 테두리 있는 선택 항목은 클릭 영역 안에 배치합니다. */
  edgeAlignment?: 'mark' | 'control';
  sx?: SxProps<Theme>;
  controlSx?: SxProps<Theme>;
  'data-testid'?: string;
};

/**
 * Checkbox, Radio, Switch의 label 연결과 boolean change 계약을 통일합니다.
 * label 없는 control은 ariaLabel/ariaLabelledBy를 제공하거나 decorative로 선언해야 합니다.
 */
export function ChoiceControl({
  kind,
  label,
  ariaLabel,
  ariaLabelledBy,
  ariaDescribedBy,
  decorative = false,
  checked,
  indeterminate,
  disabled,
  name,
  value,
  size,
  onCheckedChange,
  labelPlacement,
  edgeAlignment = 'mark',
  sx,
  controlSx,
  'data-testid': testId,
}: ChoiceControlProps) {
  if (import.meta.env.DEV && label === undefined && !ariaLabel && !ariaLabelledBy && !decorative) {
    throw new Error('ChoiceControl without a label requires ariaLabel, ariaLabelledBy, or decorative.');
  }

  const sharedProps = {
    checked,
    disabled,
    name,
    value,
    size,
    sx: controlSx,
    onChange: onCheckedChange ? (_event: ChangeEvent<HTMLInputElement>, nextChecked: boolean) => onCheckedChange(nextChecked) : undefined,
    slotProps: {
      input: {
        'aria-label': decorative ? undefined : ariaLabel,
        'aria-labelledby': decorative ? undefined : ariaLabelledBy,
        'aria-describedby': decorative ? undefined : ariaDescribedBy,
        'aria-hidden': decorative || undefined,
        'aria-checked': kind === 'checkbox' && indeterminate ? 'mixed' as const : undefined,
        tabIndex: decorative ? -1 : undefined,
      },
    },
  };

  const control = kind === 'radio'
    ? <Radio {...sharedProps} />
    : kind === 'switch'
      ? <Switch {...sharedProps} />
      : <Checkbox {...sharedProps} indeterminate={indeterminate} />;

  if (label === undefined) return control;
  return (
    <FormControlLabel
      className={edgeAlignment === 'mark' && kind !== 'switch' && (!labelPlacement || labelPlacement === 'end') && !controlSx ? 'HeydayChoice-leadingAligned' : undefined}
      control={control}
      label={label}
      labelPlacement={labelPlacement}
      disabled={disabled}
      value={value}
      data-testid={testId}
      sx={sx}
    />
  );
}

export type ChoiceGroupProps = Omit<RadioGroupProps, 'onChange'> & {
  onValueChange: (value: string) => void;
};

/** Radio 선택지의 keyboard 이동, 이름과 단일 value 변경을 한 경계로 묶습니다. */
export function ChoiceGroup({ onValueChange, ...radioGroupProps }: ChoiceGroupProps) {
  return <RadioGroup {...radioGroupProps} onChange={(event) => onValueChange(event.target.value)} />;
}
